import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Modal, Button, Table, Tooltip, Typography, Space, Input, message, Tag, Popconfirm, Select, Alert } from 'antd';
import { MessageOutlined, AmazonOutlined, SendOutlined, StopOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { sendBulkMessage } from '../actions';

const { Text } = Typography;
const { TextArea } = Input;

const SendBulkMessageModal = ({ visible, onCancel, onConfirm, selectedOrderItems, loading }) => {
    const dispatch = useDispatch();
    const { autoMessages } = useSelector(state => state.dataCommon);
    const [messageContent, setMessageContent] = useState('');
    const [sending, setSending] = useState(false);
    const textAreaRef = useRef(null);

    // Use autoMessages from Redux
    const availableTemplates = autoMessages || [];

    // Memoize dropdown options to prevent re-render issues
    const templateOptions = useMemo(() => {
        if (!availableTemplates || availableTemplates.length === 0) {
            return [{ value: 'no_templates', label: '⚠️ Chưa có mẫu tin nhắn', disabled: true }];
        }
        
        return availableTemplates.map(template => ({
            value: template.key,
            label: template.description || template.key
        }));
    }, [availableTemplates]);

    // Handle auto message template selection - Replace textarea content
    const handleTemplateSelect = useCallback((templateKey) => {
        const selectedTemplate = autoMessages?.find(template => template.key === templateKey);
        if (selectedTemplate && selectedTemplate.value) {
            const templateContent = selectedTemplate.value;
            
            // Check if template content exceeds limit
            if (templateContent.length > 2000) {
                message.error('Template vượt quá giới hạn 2000 ký tự');
                return;
            }
            
            // Replace entire content with template
            setMessageContent(templateContent);
            message.success(`Đã áp dụng template: ${selectedTemplate.description || selectedTemplate.key}`);
            
            // Focus textarea after a short delay
            setTimeout(() => {
                const textArea = textAreaRef.current?.resizableTextArea?.textArea || textAreaRef.current;
                if (textArea) {
                    textArea.focus();
                    // Set cursor to end
                    textArea.setSelectionRange(templateContent.length, templateContent.length);
                }
            }, 100);
        }
    }, [autoMessages]);

    // Group order items by amazonOrderId to show unique orders
    const groupedOrders = useMemo(() => {
        if (!selectedOrderItems || selectedOrderItems.length === 0) return [];

        const orderMap = new Map();
        
        selectedOrderItems.forEach(item => {
            const amazonOrderId = item.order?.amazonOrderId || item.amazonOrderId;
            if (!amazonOrderId) return;

            if (!orderMap.has(amazonOrderId)) {
                orderMap.set(amazonOrderId, {
                    amazonOrderId,
                    orderItems: [],
                    customerName: item.order?.shipName || item.shipName || 'Unknown',
                    sellerSKU: item.sellerSKU || 'Unknown',
                    title: item.title || 'Unknown Product',
                    orderStatus: item.order?.orderStatus || item.orderStatus || 'Unknown'
                });
            }
            
            orderMap.get(amazonOrderId).orderItems.push(item);
        });

        return Array.from(orderMap.values());
    }, [selectedOrderItems]);

    // Separate sendable and blocked (Shipped) orders
    const sendableOrders = useMemo(() => 
        groupedOrders.filter(o => o.orderStatus !== 'Shipped'), 
        [groupedOrders]
    );
    const blockedOrders = useMemo(() => 
        groupedOrders.filter(o => o.orderStatus === 'Shipped'), 
        [groupedOrders]
    );

    // Get only sendable order item IDs (exclude Shipped orders)
    const sendableOrderItemIds = useMemo(() => {
        if (!selectedOrderItems || selectedOrderItems.length === 0) return [];
        const blockedAmazonOrderIds = new Set(blockedOrders.map(o => o.amazonOrderId));
        return selectedOrderItems
            .filter(item => {
                const amazonOrderId = item.order?.amazonOrderId || item.amazonOrderId;
                return !blockedAmazonOrderIds.has(amazonOrderId);
            })
            .map(item => item.id);
    }, [selectedOrderItems, blockedOrders]);

    const handleSendMessage = async () => {
        if (!messageContent.trim()) {
            message.error('Please enter a message');
            return;
        }

        if (messageContent.length > 2000) {
            message.error('Message content cannot exceed 2000 characters');
            return;
        }

        if (sendableOrders.length === 0) {
            message.error('No sendable orders. Shipped orders cannot receive custom messages.');
            return;
        }

        setSending(true);
        try {
            const result = await dispatch(sendBulkMessage({
                orderItemIds: sendableOrderItemIds,
                messageContent: messageContent.trim()
            }));

            if (result.success) {
                message.success(`Bulk message sent to ${sendableOrders.length} orders successfully!`);
                setMessageContent('');
                if (onConfirm) {
                    onConfirm(result);
                }
            } else {
                throw new Error(result.message || 'Failed to send bulk message');
            }
        } catch (error) {
            console.error('Error sending bulk message:', error);
            message.error(error.message || 'Failed to send bulk message');
        } finally {
            setSending(false);
        }
    };

    const handleCancel = () => {
        setMessageContent('');
        setSending(false);
        if (onCancel) {
            onCancel();
        }
    };

    const columns = [
        {
            title: '#',
            key: 'index',
            width: 50,
            align: 'center',
            render: (_, record, index) => (
                <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{index + 1}</Text>
            )
        },
        {
            title: 'Amazon Order ID',
            dataIndex: 'amazonOrderId',
            key: 'amazonOrderId',
            width: 190,
            render: (text) => (
                <Space>
                    <AmazonOutlined style={{ color: '#FF9900', fontSize: 12 }} />
                    <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Customer',
            dataIndex: 'customerName',
            key: 'customerName',
            width: 150,
            render: (text) => (
                <Text style={{ fontSize: 12 }}>{text}</Text>
            )
        },
        {
            title: 'SKU',
            dataIndex: 'sellerSKU',
            key: 'sellerSKU',
            width: 200,
            render: (text) => <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{text}</Text>
        },
        {
            title: 'Product Name',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <Text style={{ fontSize: 12 }}>{text}</Text>
                </Tooltip>
            )
        },
        {
            title: 'Items Count',
            dataIndex: 'orderItems',
            key: 'itemsCount',
            width: 100,
            align: 'center',
            render: (orderItems) => (
                <Tag color="blue" style={{ fontSize: 11 }}>
                    {orderItems.length} item{orderItems.length > 1 ? 's' : ''}
                </Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            width: 120,
            align: 'center',
            render: (status) => {
                if (status === 'Shipped') {
                    return (
                        <Tooltip title="Shipped orders cannot receive custom messages via Amazon API">
                            <Tag color="red" icon={<StopOutlined />} style={{ fontSize: 11 }}>
                                Blocked
                            </Tag>
                        </Tooltip>
                    );
                }
                return <Tag color="green" style={{ fontSize: 11 }}>{status}</Tag>;
            }
        }
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MessageOutlined style={{ color: '#1890ff' }} />
                    <span>Send Bulk Message to Customers</span>
                </div>
            }
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel} disabled={sending}>
                    Cancel
                </Button>,
                <Popconfirm
                    key="send"
                    title="Send bulk message to customers?"
                    description={
                        <div style={{ maxWidth: '300px' }}>
                            Are you sure you want to send this message to {sendableOrders.length} order(s)?
                            This action cannot be undone.
                        </div>
                    }
                    onConfirm={handleSendMessage}
                    okText="Send"
                    cancelText="Cancel"
                    disabled={!messageContent.trim() || sending || sendableOrders.length === 0}
                >
                    <Button 
                        type="primary" 
                        icon={<SendOutlined />}
                        loading={sending}
                        disabled={!messageContent.trim() || sendableOrders.length === 0}
                        // style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                    >
                        Send to {sendableOrders.length} Order{sendableOrders.length !== 1 ? 's' : ''}
                    </Button>
                </Popconfirm>
            ]}
            width='80%'
            styles={{
                body: {
                    padding: '16px'
                }
            }}
        >
            <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                    Message Content:
                </Text>
                <TextArea
                    ref={textAreaRef}
                    className="bulk-message-textarea"
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    placeholder="Type your message to customers (max 2000 characters)..."
                    maxLength={2000}
                    rows={6}
                    // showCount
                    style={{ marginBottom: 12 }}
                />
                
                {/* Auto Message Templates Dropdown */}
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Quick templates:
                        </Text>
                        <Select
                            placeholder="Select template"
                            style={{ minWidth: 300 }}
                            size="small"
                            allowClear
                            value={undefined} // Always reset after selection
                            onSelect={handleTemplateSelect}
                            onClear={() => message.info('Template selection cleared')}
                            popupMatchSelectWidth={400}
                            placement='bottomLeft'
                            options={templateOptions}
                            notFoundContent="Không tìm thấy mẫu tin nhắn"
                        />
                    </div>
                    
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {messageContent.length}/2000 characters
                    </Text>
                </div>
                
                <Text type="secondary" style={{ fontSize: 12 }}>
                    This message will be sent to all selected customers via Amazon's messaging system.
                </Text>
            </div>

            {blockedOrders.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<StopOutlined />}
                    message={`${blockedOrders.length} order(s) with status "Shipped" will be skipped`}
                    description="Amazon API does not support sending custom messages to Shipped/Delivered orders. Only predefined templates (updateFeedback, sendInvoice) are available for these orders."
                    style={{ marginBottom: 16 }}
                />
            )}

            <div style={{ marginBottom: 16 }}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>
                    Selected Orders ({groupedOrders.length} unique orders):
                </Text>
                
                <Table
                    dataSource={groupedOrders}
                    rowKey="amazonOrderId"
                    pagination={false}
                    size="small"
                    columns={columns}
                    scroll={{ y: 300 }}
                    bordered
                    rowClassName={(record) => record.orderStatus === 'Shipped' ? 'bulk-message-blocked-row' : ''}
                />
            </div>
        </Modal>
    );
};

export default SendBulkMessageModal;